/**
 * Cart checkbox upsell — adds/removes an optional product (e.g. shipping protection).
 */
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogOpenEvent } from '@theme/dialog';
import { lineForVariant, shouldAutoApplyDefault } from '@theme/cart-upsell-checkbox-logic';
import {
  morphDrawerCartItemsFromSection,
  refreshDrawerSections,
  syncCartDrawerEmptyState,
} from '@theme/cart-drawer-utils';

/** Tracks default auto-add per variant for this page session (survives cart drawer morphs). */
const defaultApplyStarted = new Set();

/** User manually removed upsell — do not auto-add again until they re-enable. */
const userOptedOut = new Set();

/** @type {Map<string, Promise<void>>} */
const autoApplyInflight = new Map();

class CartUpsellCheckbox extends HTMLElement {
  /** @type {HTMLInputElement | null} */
  #checkbox = null;

  /** @type {boolean} */
  #busy = false;

  /** @type {((event: Event) => void) | undefined} */
  #onCartUpdate;

  /** @type {((event: Event) => void) | undefined} */
  #onDrawerOpen;

  /** @type {((event: Event) => void) | undefined} */
  #onCartAdd;

  connectedCallback() {
    this.#checkbox = this.querySelector('[data-cart-upsell-checkbox]');
    if (!this.#checkbox || !this.dataset.variantId) return;

    this.#checkbox.addEventListener('change', this.#onToggle);

    this.#onCartUpdate = (event) => {
      if (this.#busy) return;
      const cart = /** @type {CustomEvent} */ (event).detail?.resource;
      this.#syncCheckbox(cart);
    };
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);

    this.#onDrawerOpen = (event) => {
      if (!(event.target instanceof Element) || !event.target.closest('cart-drawer-component')) return;
      this.tryAutoApply();
    };
    document.addEventListener(DialogOpenEvent.eventName, this.#onDrawerOpen);

    this.#onCartAdd = (event) => {
      if (!(event instanceof CartAddEvent) || !event.detail?.data?.openDrawer) return;
      this.tryAutoApply(event.detail?.resource);
    };
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartAdd);

    this.#syncCheckbox();
  }

  disconnectedCallback() {
    this.#checkbox?.removeEventListener('change', this.#onToggle);
    if (this.#onCartUpdate) {
      document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    }
    if (this.#onDrawerOpen) {
      document.removeEventListener(DialogOpenEvent.eventName, this.#onDrawerOpen);
    }
    if (this.#onCartAdd) {
      document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartAdd);
    }
  }

  /**
   * Public hook for post-morph sync (called from cart-drawer-utils).
   * @param {Object} [cart]
   */
  syncFromCart(cart) {
    return this.#syncCheckbox(cart);
  }

  /**
   * Applies default-checked setting when the drawer opens or after add-to-cart.
   * @param {Object} [cart]
   */
  tryAutoApply(cart) {
    const variantId = this.dataset.variantId;
    if (!variantId) return Promise.resolve();

    const existing = autoApplyInflight.get(variantId);
    if (existing) return existing;

    const task = this.#maybeAutoApply(cart).finally(() => {
      if (autoApplyInflight.get(variantId) === task) {
        autoApplyInflight.delete(variantId);
      }
    });

    autoApplyInflight.set(variantId, task);
    return task;
  }

  #onToggle = async () => {
    if (this.#busy || !this.#checkbox) return;

    const variantId = this.dataset.variantId;
    if (!variantId) return;

    this.#busy = true;
    this.#checkbox.disabled = true;

    try {
      if (this.#checkbox.checked) {
        userOptedOut.delete(variantId);
        await this.#addToCart();
      } else {
        userOptedOut.add(variantId);
        await this.#removeFromCart();
      }
    } catch (error) {
      console.error(error);
      await this.#syncCheckbox();
    } finally {
      this.#busy = false;
      if (this.#checkbox) this.#checkbox.disabled = false;
    }
  };

  async #maybeAutoApply(cart) {
    if (this.#busy || !this.#checkbox) return;

    const variantId = this.dataset.variantId;
    if (!variantId) return;

    const cartData = await this.#resolveCart(cart);
    this.#applyCheckboxState(cartData);

    const inCart = this.#checkbox.checked;
    const shouldApply = shouldAutoApplyDefault({
      checkedDefault: this.dataset.checkedDefault === 'true',
      inCart,
      optedOut: userOptedOut.has(variantId),
      defaultStarted: defaultApplyStarted.has(variantId),
    });

    if (!shouldApply) return;

    defaultApplyStarted.add(variantId);

    try {
      await this.#addToCart(cartData);
    } catch (error) {
      console.error(error);
      defaultApplyStarted.delete(variantId);
      await this.#syncCheckbox();
    }
  }

  /** @returns {string[]} */
  #getSectionIds() {
    const sectionsToUpdate = new Set();
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });
    return Array.from(sectionsToUpdate);
  }

  /**
   * @param {Object} cart
   * @param {Record<string, string> | undefined} sections
   */
  #dispatchCartEvent(cart, sections) {
    document.dispatchEvent(
      new CartUpdateEvent(cart, this.id, {
        source: 'cart-upsell-checkbox',
        itemCount: cart?.item_count,
        variantId: this.dataset.variantId,
        sections,
      })
    );
  }

  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`);
    return response.json();
  }

  /**
   * @param {Object} [cart]
   * @returns {Promise<Object>}
   */
  async #resolveCart(cart) {
    if (cart?.items) return cart;
    return this.#fetchCart();
  }

  /**
   * @param {{ items?: Array<{ variant_id: number }> }} cart
   * @returns {number | null}
   */
  #lineForVariant(cart) {
    return lineForVariant(cart, this.dataset.variantId || '');
  }

  /**
   * @param {{ items?: Array<{ variant_id: number }>, item_count?: number }} cart
   */
  #applyCheckboxState(cart) {
    if (!this.#checkbox) return;

    const line = this.#lineForVariant(cart);
    const inCart = line !== null;

    this.classList.toggle('cart-upsell-checkbox--selected', inCart);
    this.#checkbox.checked = inCart;
    this.#checkbox.setAttribute('aria-checked', inCart ? 'true' : 'false');
    this.hidden = !cart.item_count;

    if (line) {
      this.dataset.cartLine = String(line);
    } else {
      delete this.dataset.cartLine;
    }
  }

  /**
   * @param {Object} [cart]
   */
  async #syncCheckbox(cart) {
    if (!this.#checkbox || this.#busy) return;
    const cartData = await this.#resolveCart(cart);
    this.#applyCheckboxState(cartData);
  }

  /**
   * @param {Object} cart
   * @param {Record<string, string> | undefined} sections
   */
  #applySections(cart, sections) {
    const drawer = document.querySelector('cart-items-component[data-drawer]');
    const sectionId = drawer?.dataset?.sectionId;
    const sectionHtml = sectionId && sections?.[sectionId];

    if (sectionHtml && morphDrawerCartItemsFromSection(sectionHtml, cart)) {
      return true;
    }

    return refreshDrawerSections(sections, cart);
  }

  /**
   * @param {Object} cart
   * @param {Record<string, string> | undefined} sections
   */
  async #commitCartChange(cart, sections) {
    await this.#applySections(cart, sections);
    this.#applyCheckboxState(cart);
    this.#dispatchCartEvent(cart, sections);
    syncCartDrawerEmptyState();
  }

  /**
   * @param {Object} [knownCart]
   */
  async #addToCart(knownCart) {
    const existingCart = await this.#resolveCart(knownCart);
    if (this.#lineForVariant(existingCart) !== null) {
      this.#applyCheckboxState(existingCart);
      return;
    }

    const sectionIds = this.#getSectionIds();
    const variantId = Number(this.dataset.variantId);

    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });

    const data = await response.json();

    if (data.status) {
      throw new Error(data.message || 'Failed to add upsell product');
    }

    const cart = await this.#fetchCart();
    await this.#commitCartChange(cart, data.sections);
  }

  async #removeFromCart() {
    let line = Number(this.dataset.cartLine);

    // Skip the lookup fetch entirely when we already know the line (the common
    // case — toggling off something already in cart) instead of always
    // fetching the cart up front just to re-derive a line we already have.
    if (!Number.isFinite(line) || line <= 0) {
      const cart = await this.#fetchCart();
      line = this.#lineForVariant(cart) ?? 0;

      if (!line) {
        this.#applyCheckboxState(cart);
        return;
      }
    }

    const sectionIds = this.#getSectionIds();
    const response = await fetch(Theme.routes.cart_change_url, {
      ...fetchConfig('json'),
      method: 'POST',
      body: JSON.stringify({
        line,
        quantity: 0,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });

    const data = JSON.parse(await response.text());

    if (data.errors) {
      throw new Error(typeof data.errors === 'string' ? data.errors : 'Failed to remove upsell product');
    }

    const updatedCart = await this.#fetchCart();
    await this.#commitCartChange(updatedCart, data.sections);
  }
}

if (!customElements.get('cart-upsell-checkbox')) {
  customElements.define('cart-upsell-checkbox', CartUpsellCheckbox);
}
